import {
  podeRetomar,
  type AvaliarAtendimento,
  type DestinoUpload,
  type EstadoChat,
  type IniciarAtendimento,
  type InscricaoPushEntrada,
  type MensagemChat,
  type PoliticaPrivacidade,
  type SessaoChat,
} from '@mymaxx/shared';
import { api } from './api';

/** Onde guardamos o token da sessão do chat: permite retomar o atendimento ao voltar na aba. */
const CHAVE_TOKEN = 'mymaxx.chat.token';

export function lerTokenChat(): string | null {
  try {
    return localStorage.getItem(CHAVE_TOKEN);
  } catch {
    return null;
  }
}

export function guardarTokenChat(token: string | null) {
  try {
    if (token) localStorage.setItem(CHAVE_TOKEN, token);
    else localStorage.removeItem(CHAVE_TOKEN);
  } catch {
    /* navegador sem storage (aba anônima): a sessão vale só enquanto a página estiver aberta */
  }
}

/**
 * Lembrança do aparelho: depois que o cliente se identifica uma vez, o chat guarda um
 * token opaco para oferecer "é você, Fulano?" na próxima visita, em vez de pedir o
 * documento de novo. Não é login — só poupa a digitação.
 */
const CHAVE_DISPOSITIVO = 'mymaxx.chat.dispositivo';

export function lerDispositivo(): string | null {
  try {
    return localStorage.getItem(CHAVE_DISPOSITIVO);
  } catch {
    return null;
  }
}

export function guardarDispositivo(token: string | null) {
  try {
    if (token) localStorage.setItem(CHAVE_DISPOSITIVO, token);
    else localStorage.removeItem(CHAVE_DISPOSITIVO);
  } catch {
    /* sem storage: o aparelho simplesmente não lembra */
  }
}

/** Guarda a lembrança quando o servidor manda uma nova (o cliente acabou de se identificar). */
function anotarLembranca<T extends { dispositivoToken?: string }>(resposta: T): T {
  if (resposta.dispositivoToken) guardarDispositivo(resposta.dispositivoToken);
  return resposta;
}

/** O chat se autentica pelo token da conversa, não pela conta — funciona sem login. */
const daConversa = () => {
  const token = lerTokenChat();
  const dispositivo = lerDispositivo();
  const headers: Record<string, string> = {
    ...(token ? { 'x-mymaxx-conversa': token } : {}),
    ...(dispositivo ? { 'x-mymaxx-dispositivo': dispositivo } : {}),
  };
  return { autenticado: false as const, headers };
};

/** Duas chamadas simultâneas abririam dois protocolos (StrictMode, clique duplo): compartilha a mesma. */
let aberturaEmAndamento: Promise<SessaoChat> | null = null;

export const chatApi = {
  /**
   * Retoma o atendimento desta máquina ou abre um novo. Toda a decisão fica aqui (e não na tela)
   * para que uma segunda chamada — StrictMode, clique duplo, duas abas — encontre o token já
   * gravado e entre na mesma conversa, em vez de gastar outro protocolo.
   */
  abrirOuRetomar(dados: IniciarAtendimento): Promise<SessaoChat> {
    aberturaEmAndamento ??= (async () => {
      if (lerTokenChat()) {
        try {
          const [estado, mensagens] = await Promise.all([chatApi.estado(), chatApi.mensagens()]);
          // Conversa viva (ou encerrada esperando nota) é retomada; encerrada e sem
          // pendência não, senão o cliente fica preso na tela do atendimento que acabou.
          if (podeRetomar(estado)) return { ...estado, token: lerTokenChat()!, mensagens };
          guardarTokenChat(null);
        } catch {
          guardarTokenChat(null); // conversa sumiu ou token venceu: começa uma nova
        }
      }
      const sessao = anotarLembranca(
        await api.post<SessaoChat>('/v1/chat/abrir', {
          ...dados,
          setorId: dados.setorId,
          // O aparelho conta quem já se identificou aqui; a triagem decide se pergunta.
          dispositivoToken: lerDispositivo() ?? undefined,
        }),
      );
      guardarTokenChat(sessao.token);
      return sessao;
    })().finally(() => {
      aberturaEmAndamento = null;
    });
    return aberturaEmAndamento;
  },

  estado: () => api.get<EstadoChat>('/v1/chat/estado', daConversa()),

  mensagens: () => api.get<MensagemChat[]>('/v1/chat/mensagens', daConversa()),

  /** Chave pública do aviso de mensagem nova. Vazia = push desligado no servidor. */
  chavePush: () =>
    api.get<{ chave: string }>('/v1/chat/push/chave', { autenticado: false as const }),

  inscreverPush: (inscricao: InscricaoPushEntrada) =>
    api.post<void>('/v1/chat/push/inscrever', inscricao, daConversa()),

  privacidade: () =>
    api.get<PoliticaPrivacidade>('/v1/chat/privacidade', { autenticado: false as const }),

  enviar: (texto: string, respondeAId?: string) =>
    api
      .post<EstadoChat>(
        '/v1/chat/mensagens',
        { texto, ...(respondeAId ? { respondeAId } : {}) },
        daConversa(),
      )
      .then(anotarLembranca),

  /** "Não sou eu": o aparelho esquece o cliente anterior, aqui e no servidor. */
  async naoSouEu(): Promise<void> {
    const token = lerDispositivo();
    guardarDispositivo(null);
    if (token) {
      await api
        .post('/v1/chat/esquecer-dispositivo', { dispositivoToken: token }, { autenticado: false })
        .catch(() => undefined);
    }
  },

  encerrar: () => api.post<EstadoChat>('/v1/chat/encerrar', undefined, daConversa()),

  avaliar: (dados: AvaliarAtendimento) =>
    api.post<EstadoChat>('/v1/chat/avaliar', dados, daConversa()),

  /** Anexo em dois passos: pede a URL, manda o arquivo direto ao storage e confirma. */
  async enviarArquivo(arquivo: File, legenda?: string): Promise<EstadoChat> {
    const destino = await api.post<DestinoUpload>(
      '/v1/chat/anexos/preparar',
      { nome: arquivo.name, mimeType: arquivo.type, tamanho: arquivo.size },
      daConversa(),
    );
    const envio = await fetch(destino.url, {
      method: 'PUT',
      body: arquivo,
      headers: { 'Content-Type': arquivo.type },
    });
    if (!envio.ok) throw new Error('Não consegui enviar o arquivo. Tente de novo.');

    return api.post<EstadoChat>(
      '/v1/chat/anexos',
      {
        chave: destino.chave,
        nome: arquivo.name,
        mimeType: arquivo.type,
        legenda: legenda ?? '',
      },
      daConversa(),
    );
  },
};
